import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, Shuffle, X } from "lucide-react";
import { ALBUM_COVER, TRACKS, scrollToId } from "@/data";

export const PREVIEW_PLAY_EVENT = "lc:preview-play";
export const TRACKLIST_PLAY_EVENT = "lc:tracklist-play";

export default function RandomPreview() {
  const [index] = useState(() => Math.floor(Math.random() * TRACKS.length));
  const [playing, setPlaying] = useState(false);
  const [waiting, setWaiting] = useState(true); // autoplay blocked, waiting for first interaction
  const [dismissed, setDismissed] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const track = TRACKS[index];

  useEffect(() => {
    const audio = new Audio(track.preview);
    audio.preload = "auto";
    audio.volume = 0.8;
    audioRef.current = audio;
    const onTime = () => setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    const onEnd = () => {
      setPlaying(false);
      setProgress(0);
    };
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnd);

    const start = () =>
      audio
        .play()
        .then(() => {
          setPlaying(true);
          setWaiting(false);
          window.dispatchEvent(new CustomEvent(PREVIEW_PLAY_EVENT));
        })
        .catch(() => setWaiting(true));

    // 1) try autoplay (works only if the browser already trusts the site)
    start();
    // 2) otherwise start on the first user gesture anywhere in the page
    const gestures = ["pointerdown", "keydown", "touchend"];
    const onGesture = () => {
      gestures.forEach((g) => window.removeEventListener(g, onGesture));
      if (!audio.paused || audio.ended) return;
      start();
    };
    gestures.forEach((g) => window.addEventListener(g, onGesture, { passive: true }));

    const onTracklist = () => {
      audio.pause();
      setPlaying(false);
    };
    window.addEventListener(TRACKLIST_PLAY_EVENT, onTracklist);

    return () => {
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnd);
      gestures.forEach((g) => window.removeEventListener(g, onGesture));
      window.removeEventListener(TRACKLIST_PLAY_EVENT, onTracklist);
    };
  }, [track.preview]);

  const toggle = (e) => {
    e.stopPropagation();
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => {
        setPlaying(true);
        setWaiting(false);
        window.dispatchEvent(new CustomEvent(PREVIEW_PLAY_EVENT));
      });
    }
  };

  const dismiss = (e) => {
    e.stopPropagation();
    audioRef.current?.pause();
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {!dismissed && (
        <motion.aside
          data-testid="random-preview-player"
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 1.2 }}
          className="fixed bottom-4 left-4 z-40 w-[calc(100vw-2rem)] max-w-sm border border-red-900/50 bg-[#0B0B0B]/95 shadow-[0_10px_40px_rgba(0,0,0,0.7)] backdrop-blur sm:bottom-6 sm:left-6"
        >
          <div className="flex items-center gap-3 p-3">
            <button
              data-testid="random-preview-toggle"
              onClick={toggle}
              aria-label={playing ? "Pausa estratto" : "Ascolta estratto"}
              className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden border border-neutral-800"
            >
              <img src={ALBUM_COVER} alt="" className="absolute inset-0 h-full w-full object-cover opacity-60" />
              <span className="relative text-white">
                {playing ? <Pause className="h-4 w-4 fill-current" /> : <Play className="h-4 w-4 fill-current" />}
              </span>
            </button>
            <button
              data-testid="random-preview-info"
              onClick={() => scrollToId("album")}
              className="min-w-0 flex-1 text-left"
            >
              <p className="flex items-center gap-1.5 font-mono-tac text-[9px] uppercase tracking-[0.3em] text-red-600">
                <Shuffle className="h-3 w-3" />
                {waiting && !playing ? "Estratto casuale · tocca per ascoltare" : "Estratto casuale dall'EP"}
              </p>
              <p data-testid="random-preview-title" className="truncate text-sm font-medium text-neutral-100">
                {track.title}
              </p>
            </button>
            <button
              data-testid="random-preview-close"
              onClick={dismiss}
              aria-label="Chiudi player"
              className="flex h-8 w-8 shrink-0 items-center justify-center text-neutral-500 transition-colors duration-200 hover:text-red-500"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <span className="block h-[2px] w-full bg-neutral-900">
            <span className="block h-full bg-[#C8102E]" style={{ width: `${progress * 100}%` }} />
          </span>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
